import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import supabase from '../utils/supabaseClient';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './LessonViewer.css';

const renderLatex = (text) => {
    if (!text) return '';
    let result = text.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
        try {
            return katex.renderToString(latex.trim(), { displayMode: true, throwOnError: false });
        } catch {
            return `$$${latex}$$`;
        }
    });

    result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
        try {
            return katex.renderToString(latex.trim(), { displayMode: false, throwOnError: false });
        } catch {
            return `$${latex}$`;
        }
    });

    return result.replace(/\n/g, '<br/>');
};

const LessonViewer = () => {
    const { isDarkTheme } = useTheme();
    const { id } = useParams();
    const navigate = useNavigate();

    const [lesson, setLesson] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadLesson = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('lessons')
                    .select('*')
                    .eq('id', id)
                    .eq('status', 'approved')
                    .maybeSingle();

                if (error) throw error;

                if (!data) {
                    // fallback if approved status is not used yet
                    const { data: fallbackData, error: fallbackError } = await supabase
                        .from('lessons')
                        .select('*')
                        .eq('id', id)
                        .maybeSingle();
                    if (fallbackError) throw fallbackError;
                    setLesson(fallbackData || null);
                } else {
                    setLesson(data);
                }
            } catch (err) {
                console.error('[LessonViewer] Load error:', err);
                setLesson(null);
            } finally {
                setLoading(false);
            }
        };

        loadLesson();
    }, [id]);

    const renderedFront = useMemo(() => renderLatex(lesson?.front_content || ''), [lesson?.front_content]);
    const renderedHint = useMemo(() => renderLatex(lesson?.hint_content || ''), [lesson?.hint_content]);
    const renderedBack = useMemo(() => renderLatex(lesson?.back_content || ''), [lesson?.back_content]);

    if (loading) {
        return (
            <div className={`lesson-viewer ${isDarkTheme ? 'dark' : 'light'}`}>
                <div className="lesson-viewer-container">
                    <div className="lesson-viewer-loading">Loading lesson...</div>
                </div>
            </div>
        );
    }

    if (!lesson) {
        return (
            <div className={`lesson-viewer ${isDarkTheme ? 'dark' : 'light'}`}>
                <div className="lesson-viewer-container">
                    <button className="lesson-viewer-back" onClick={() => navigate('/learn')}>← Back to lessons</button>
                    <div className="lesson-viewer-empty card">Lesson not found.</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`lesson-viewer ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="lesson-viewer-container">
                <button className="lesson-viewer-back" onClick={() => navigate('/learn')}>← Back to lessons</button>

                <header className="lesson-viewer-header card">
                    <h1>{lesson.title || 'Untitled Lesson'}</h1>
                    <div className="lesson-viewer-meta">
                        {Array.isArray(lesson.tags) && lesson.tags.map((tag, i) => (
                            <span key={i} className="lesson-viewer-tag">{tag}</span>
                        ))}
                    </div>
                </header>

                <section className="lesson-viewer-section card">
                    <h2>Lesson</h2>
                    <div dangerouslySetInnerHTML={{ __html: renderedFront || '(empty)' }} />
                    {Array.isArray(lesson.front_images) && lesson.front_images.length > 0 && (
                        <div className="lesson-viewer-images">
                            {lesson.front_images.map((url, i) => (
                                <img key={i} src={url} alt={`Lesson figure ${i + 1}`} />
                            ))}
                        </div>
                    )}
                </section>

                {lesson.hint_content && (
                    <section className="lesson-viewer-section card">
                        <h2>Notes</h2>
                        <div dangerouslySetInnerHTML={{ __html: renderedHint }} />
                    </section>
                )}

                {lesson.back_content && (
                    <section className="lesson-viewer-section card">
                        <h2>Summary</h2>
                        <div dangerouslySetInnerHTML={{ __html: renderedBack }} />
                    </section>
                )}
            </div>
        </div>
    );
};

export default LessonViewer;
